import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CheckCircle, WhatsappLogo } from "@phosphor-icons/react";
import { api, waLink, CONTACT_EMAIL } from "@/lib/api";

export default function BookingSuccess() {
  const { t, i18n } = useTranslation();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const bookingId = params.get("booking");
  const [status, setStatus] = useState("checking"); // checking | paid | pending
  const [booking, setBooking] = useState(null);
  const tries = useRef(0);

  useEffect(() => {
    if (bookingId) {
      api.get(`/bookings/${bookingId}/instructions`)
        .then((r) => { setBooking(r.data.booking); setStatus("paid"); })
        .catch(() => setStatus("pending"));
      return;
    }
    if (!sessionId) return setStatus("pending");
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          const b = await api.get(`/bookings/by-session/${sessionId}`);
          setBooking(b.data);
          setStatus("paid");
          return;
        }
      } catch (e) { /* retry */ }
      tries.current += 1;
      if (tries.current < 15) setTimeout(poll, 2000);
      else setStatus("pending");
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const money = (v) => new Intl.NumberFormat(i18n.language, { style: "currency", currency: "EUR" }).format(v);

  return (
    <div data-testid="booking-success-page" className="min-h-screen bg-sand flex items-center justify-center px-6">
      <div className="max-w-lg w-full border border-line bg-white p-10 text-center">
        {status === "checking" && (
          <>
            <div className="w-10 h-10 border-2 border-line border-t-terracotta rounded-full animate-spin mx-auto" />
            <p className="mt-6 text-sm text-ink/60">{t("success.checking")}</p>
          </>
        )}
        {status === "pending" && (
          <>
            <h1 className="font-serif text-4xl text-ink">{t("success.pending")}</h1>
            <p className="mt-4 text-sm text-ink/60">
              {booking?.pay_method === "transfer" ? t("success.sepaPending") : t("success.pendingSub")}
            </p>
          </>
        )}
        {status === "paid" && booking && (
          <>
            <CheckCircle size={52} weight="fill" className="text-[#4A6B53] mx-auto" />
            <h1 data-testid="success-title" className="font-serif text-4xl md:text-5xl text-ink mt-6">{t("success.title")}</h1>
            <p className="mt-3 text-sm text-ink/60">{t("success.sub")}</p>
            <div className="mt-8 border-y border-line py-6 text-left text-sm">
              <p className="font-serif text-2xl text-ink mb-4">{booking.apartment_name}</p>
              <div className="grid grid-cols-2 gap-y-2 text-ink/70">
                <span className="text-ink/45">{t("success.entry")}</span><span data-testid="success-checkin">{booking.checkin}</span>
                <span className="text-ink/45">{t("success.exit")}</span><span>{booking.checkout}</span>
                <span className="text-ink/45">{t("success.nights")}</span><span>{booking.nights}</span>
                <span className="text-ink/45">{t("success.total")}</span><span className="font-medium text-ink">{money(booking.total)}</span>
                <span className="text-ink/45">{t("success.rate")}</span><span data-testid="success-rate">{booking.rate_plan === "nonref" ? t("success.rateNonref") : t("success.rateFlex")}</span>
              </div>
            </div>
            <div className="flex flex-col gap-3 mt-8">
              <a
                data-testid="success-whatsapp"
                href={waLink(t("whatsapp.successMsg", { apartment: booking.apartment_name, checkin: booking.checkin, checkout: booking.checkout }))}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 py-4 bg-[#25D366] text-white text-xs uppercase tracking-[0.2em] hover:opacity-90 transition-opacity"
              >
                <WhatsappLogo size={16} weight="fill" /> {t("success.whatsapp")}
              </a>
              <Link data-testid="success-home" to="/" className="py-4 border border-ink/20 text-ink text-xs uppercase tracking-[0.2em] hover:bg-ink hover:text-sand transition-colors">
                {t("success.home")}
              </Link>
            </div>
            <div className="mt-8 border border-line p-5 text-left">
              <p className="text-[10px] uppercase tracking-[0.2em] text-ink/50">{t("success.helpTitle")}</p>
              <div className="grid grid-cols-2 gap-2 mt-3">
                <a
                  data-testid="success-refund-whatsapp"
                  href={waLink(t("whatsapp.refund", { apartment: booking.apartment_name, checkin: booking.checkin, checkout: booking.checkout }))}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-1.5 py-2.5 border border-ink/20 text-ink text-[10px] uppercase tracking-[0.15em] hover:border-[#25D366] hover:text-[#1da851] transition-colors"
                >
                  <WhatsappLogo size={13} /> {t("success.helpWa")}
                </a>
                <a
                  data-testid="success-refund-email"
                  href="/#contacto"
                  className="flex items-center justify-center gap-1.5 py-2.5 border border-ink/20 text-ink text-[10px] uppercase tracking-[0.15em] hover:border-terracotta hover:text-terracotta transition-colors"
                >
                  {t("success.helpEmail")}
                </a>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
